import { useEffect, useMemo } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { GET_SHL_LEAGUES } from '../../client/graphql/index.js';
import { selectedLeagueIdVar, setSelectedLeagueId } from '../../client/localState.js';

const isLeagueInList = (leagues, leagueId) =>
  Boolean(leagueId) && leagues.some((league) => String(league?.id) === String(leagueId));

export const useShlLeagueSelection = ({ leagueId: forcedLeagueId } = {}) => {
  const selectedLeagueIdState = useReactiveVar(selectedLeagueIdVar);
  const { data, loading: leaguesLoading } = useQuery(GET_SHL_LEAGUES, {
    fetchPolicy: 'cache-first',
  });

  const leagues = useMemo(() => data?.MDB?._SHL_?.leagues || [], [data]);
  const hasLoadedLeagues = Array.isArray(data?.MDB?._SHL_?.leagues);

  useEffect(() => {
    if (!hasLoadedLeagues) return;

    if (selectedLeagueIdState === undefined) {
      if (leagues.length) {
        setSelectedLeagueId(leagues[0].id);
        return;
      }
      setSelectedLeagueId(null);
      return;
    }

    if (selectedLeagueIdState && !isLeagueInList(leagues, selectedLeagueIdState)) {
      setSelectedLeagueId(leagues[0]?.id || null);
    }
  }, [hasLoadedLeagues, leagues, selectedLeagueIdState]);

  const selectedLeagueId = selectedLeagueIdState === undefined ? null : selectedLeagueIdState;

  const resolvedLeagueId = useMemo(() => {
    if (forcedLeagueId) return forcedLeagueId;
    if (!selectedLeagueId) return null;
    return isLeagueInList(leagues, selectedLeagueId) ? String(selectedLeagueId) : null;
  }, [forcedLeagueId, leagues, selectedLeagueId]);

  return {
    leagues,
    leaguesLoading,
    selectedLeagueId,
    resolvedLeagueId,
    setSelectedLeagueId,
  };
};
